import type { ContentProposal } from '@shared/contentProposal'
import { useEffect, useMemo, useState } from 'react'

import { CONTENT_PROPOSALS_CHANGED, listContentProposals } from '../../../services/contentProposals'

/** Focus refresh covers a return from Codex; polling covers proposals arriving while reading. */
export function useContentProposals(bookId: string | undefined, enabled: boolean, conversationId?: string) {
  const [items, setItems] = useState<ContentProposal[]>([])
  const [error, setError] = useState('')
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    setItems([])
    setLoaded(false)
    setError('')
    if (!bookId || !enabled) {
      return
    }
    let alive = true
    let requestId = 0
    const refresh = async () => {
      if (document.hidden) {
        return
      }
      const id = ++requestId
      try {
        const next = await listContentProposals(bookId, conversationId)
        if (alive && id === requestId) {
          setItems((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next))
          setError('')
          setLoaded(true)
        }
      } catch (error) {
        if (alive && id === requestId) {
          setError(error instanceof Error ? error.message : 'טעינת ההצעות נכשלה')
        }
      }
    }
    const notify = () => {
      void refresh()
    }
    notify()
    const timer = window.setInterval(notify, 15000)
    window.addEventListener('focus', notify)
    window.addEventListener(CONTENT_PROPOSALS_CHANGED, notify)
    document.addEventListener('visibilitychange', notify)
    return () => {
      alive = false
      window.clearInterval(timer)
      window.removeEventListener('focus', notify)
      window.removeEventListener(CONTENT_PROPOSALS_CHANGED, notify)
      document.removeEventListener('visibilitychange', notify)
    }
  }, [bookId, enabled, conversationId])
  const proposals = useMemo(
    () => (conversationId ? items.filter((item) => item.conversationId === conversationId) : items),
    [items, conversationId],
  )
  return {
    proposals,
    proposalError: error,
    proposalsLoaded: loaded,
  }
}
